import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ADMIN_ROLES } from '../../redux/selectors';
import {
  setSavvybotActiveSessionId,
  setSavvybotMessages,
  appendSavvybotMessage,
  filterSavvybotMessage,
} from '../../redux/uiReducer';
import { useRateLimit } from '../../utils/useRateLimit';
import { savvybotAsk, savvybotGetSessions, savvybotGetMessages } from '../../utils/api';
import './SavvyBot.scss';

const BOT_LABEL = (
  <>
    SavvyBot <em>3PL</em>
  </>
);

const VIZ_MAP = {
  bar_chart: 'bar',
  bar: 'bar',
  line_chart: 'line',
  line: 'line',
  table: 'table',
  kpi: 'table',
  chart: 'bar',
};

function normalizeViz(raw) {
  if (!raw) return null;
  return VIZ_MAP[raw] ?? null;
}

function normalizeAskResponse(res) {
  const vizPayload = res.visualization ?? {};
  const vizType = typeof vizPayload === 'string' ? vizPayload : (vizPayload.visualization ?? null);
  const columns = Array.isArray(res.columns)
    ? res.columns
    : Array.isArray(vizPayload.columns)
      ? vizPayload.columns
      : [];
  const data = Array.isArray(res.data)
    ? res.data
    : Array.isArray(vizPayload.data)
      ? vizPayload.data
      : [];
  const nav = res.navigation ?? {};
  return {
    role: 'assistant',
    id: res.messageId ?? res.id ?? `bot-${Date.now()}`,
    intent: res.intent ?? 'refusal',
    prose: res.answer ?? res.prose ?? res.message ?? res.response ?? '',
    visualization: normalizeViz(vizType),
    columns,
    data,
    citations: res.citations ?? [],
    navigation: nav.path || nav.route ? { route: nav.path ?? nav.route, label: nav.label } : null,
  };
}

function normalizeHistoryMessage(msg) {
  if (msg.role === 'user') {
    return {
      role: 'user',
      content: msg.content ?? msg.question ?? '',
      id: msg.id ?? msg.messageId,
    };
  }
  const vizPayload = msg.visualization ?? {};
  const vizType = typeof vizPayload === 'string' ? vizPayload : (vizPayload.visualization ?? null);
  const columns = Array.isArray(msg.columns)
    ? msg.columns
    : Array.isArray(vizPayload.columns)
      ? vizPayload.columns
      : [];
  const data = Array.isArray(msg.data)
    ? msg.data
    : Array.isArray(vizPayload.data)
      ? vizPayload.data
      : [];
  const nav = msg.navigation ?? {};
  return {
    role: 'assistant',
    id: msg.id ?? msg.messageId,
    intent: msg.intent ?? 'refusal',
    prose: msg.answer ?? msg.content ?? msg.prose ?? '',
    visualization: normalizeViz(vizType),
    columns,
    data,
    citations: msg.citations ?? [],
    navigation: nav.path || nav.route ? { route: nav.path ?? nav.route, label: nav.label } : null,
  };
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

function CitationChip({ citation, helpBase }) {
  const [expanded, setExpanded] = useState(false);
  const slug = citation.slug ?? citation.id ?? '';
  const href = `${helpBase}/${slug}`;
  return (
    <div className="savvybot-citation">
      <button
        className="savvybot-citation__chip"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        title={citation.excerpt || citation.title || slug}>
        {citation.title || slug}
      </button>
      {expanded && citation.excerpt && (
        <div className="savvybot-citation__excerpt">{citation.excerpt}</div>
      )}
      <a href={href} target="_blank" rel="noopener noreferrer" className="savvybot-citation__link">
        Open ↗
      </a>
    </div>
  );
}

function DataRenderer({ msg }) {
  const viz = msg.visualization;
  const columns = msg.columns ?? [];
  const data = msg.data ?? [];

  const chartData =
    data.length > 0 && columns.length > 0
      ? data.map((row) => {
          const obj = {};
          columns.forEach((col, i) => {
            obj[col] = row[i] ?? row[col] ?? null;
          });
          return obj;
        })
      : [];

  const numericCols = columns.filter((col) => {
    if (chartData.length === 0) return false;
    return chartData.some((row) => typeof row[col] === 'number');
  });
  const catCol = columns.find((col) => !numericCols.includes(col)) ?? columns[0] ?? 'value';

  return (
    <div className="savvybot-data">
      {msg.prose && <p className="savvybot-prose">{msg.prose}</p>}
      {chartData.length > 0 &&
        numericCols.length > 0 &&
        (viz === 'bar' || viz === 'line' || viz === 'chart') && (
          <div className="savvybot-chart">
            <ResponsiveContainer width="100%" height={200}>
              {viz === 'line' ? (
                <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis
                    dataKey={catCol}
                    tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }}
                  />
                  <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} width={40} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--card)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      color: 'var(--foreground)',
                      fontSize: 12,
                    }}
                  />
                  {numericCols.map((col, i) => (
                    <Line
                      key={col}
                      type="monotone"
                      dataKey={col}
                      stroke={['#2e5bff', '#3ec09a', '#ffb400'][i % 3]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              ) : (
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis
                    dataKey={catCol}
                    tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }}
                  />
                  <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} width={40} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--card)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      color: 'var(--foreground)',
                      fontSize: 12,
                    }}
                  />
                  {numericCols.map((col, i) => (
                    <Bar
                      key={col}
                      dataKey={col}
                      fill={['#2e5bff', '#3ec09a', '#ffb400'][i % 3]}
                      radius={[3, 3, 0, 0]}
                    />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      {chartData.length > 0 && (viz === 'table' || (!viz && numericCols.length === 0)) && (
        <div className="savvybot-table-wrap">
          <table className="savvybot-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, i) => (
                <tr key={i}>
                  {columns.map((col) => (
                    <td key={col}>{row[col] ?? '—'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {chartData.length > 0 && !viz && numericCols.length > 0 && (
        <div className="savvybot-table-wrap">
          <table className="savvybot-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, i) => (
                <tr key={i}>
                  {columns.map((col) => (
                    <td key={col}>{row[col] ?? '—'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DocsRenderer({ msg, helpBase }) {
  const citations = msg.citations ?? [];
  return (
    <div className="savvybot-docs">
      {msg.prose && <p className="savvybot-prose">{msg.prose}</p>}
      {citations.length > 0 && (
        <div className="savvybot-citations">
          {citations.map((c, i) => (
            <CitationChip key={c.slug ?? c.id ?? i} citation={c} helpBase={helpBase} />
          ))}
        </div>
      )}
    </div>
  );
}

function NavigationRenderer({ msg }) {
  const navigate = useNavigate();
  const nav = msg.navigation ?? {};
  return (
    <div className="savvybot-navigation">
      {msg.prose && <p className="savvybot-prose">{msg.prose}</p>}
      {nav.route && (
        <button className="savvybot-nav-cta" onClick={() => navigate(nav.route)}>
          Open {nav.label || nav.route}
        </button>
      )}
    </div>
  );
}

function MixedRenderer({ msg, helpBase }) {
  const navigate = useNavigate();
  const citations = msg.citations ?? [];
  const nav = msg.navigation ?? {};
  const viz = msg.visualization;
  const columns = msg.columns ?? [];
  const data = msg.data ?? [];

  const chartData =
    data.length > 0 && columns.length > 0
      ? data.map((row) => {
          const obj = {};
          columns.forEach((col, i) => {
            obj[col] = row[i] ?? row[col] ?? null;
          });
          return obj;
        })
      : [];
  const numericCols = columns.filter((col) =>
    chartData.some((row) => typeof row[col] === 'number'),
  );
  const catCol = columns.find((col) => !numericCols.includes(col)) ?? columns[0];

  return (
    <div className="savvybot-mixed">
      {msg.prose && <p className="savvybot-prose">{msg.prose}</p>}
      {chartData.length > 0 && numericCols.length > 0 && viz && viz !== 'table' && (
        <div className="savvybot-chart">
          <ResponsiveContainer width="100%" height={180}>
            {viz === 'line' ? (
              <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey={catCol} tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} />
                <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} width={40} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    color: 'var(--foreground)',
                    fontSize: 12,
                  }}
                />
                {numericCols.map((col, i) => (
                  <Line
                    key={col}
                    type="monotone"
                    dataKey={col}
                    stroke={['#2e5bff', '#3ec09a', '#ffb400'][i % 3]}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            ) : (
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey={catCol} tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} />
                <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} width={40} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    color: 'var(--foreground)',
                    fontSize: 12,
                  }}
                />
                {numericCols.map((col, i) => (
                  <Bar
                    key={col}
                    dataKey={col}
                    fill={['#2e5bff', '#3ec09a', '#ffb400'][i % 3]}
                    radius={[3, 3, 0, 0]}
                  />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
      {chartData.length > 0 && (viz === 'table' || numericCols.length === 0) && (
        <div className="savvybot-table-wrap">
          <table className="savvybot-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, i) => (
                <tr key={i}>
                  {columns.map((col) => (
                    <td key={col}>{row[col] ?? '—'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {citations.length > 0 && (
        <div className="savvybot-citations">
          {citations.map((c, i) => (
            <CitationChip key={c.slug ?? c.id ?? i} citation={c} helpBase={helpBase} />
          ))}
        </div>
      )}
      {nav.route && (
        <button className="savvybot-nav-cta" onClick={() => navigate(nav.route)}>
          Open {nav.label || nav.route}
        </button>
      )}
    </div>
  );
}

function RefusalRenderer({ msg }) {
  return (
    <div className="savvybot-refusal">
      <p className="savvybot-prose savvybot-prose--muted">
        {msg.prose || msg.message || "I can't help with that."}
      </p>
    </div>
  );
}

function AssistantMessage({ msg, helpBase }) {
  const intent = msg.intent ?? 'refusal';
  if (intent === 'data') return <DataRenderer msg={msg} />;
  if (intent === 'docs') return <DocsRenderer msg={msg} helpBase={helpBase} />;
  if (intent === 'navigation') return <NavigationRenderer msg={msg} />;
  if (intent === 'mixed') return <MixedRenderer msg={msg} helpBase={helpBase} />;
  return <RefusalRenderer msg={msg} />;
}

function SavvyBot({
  portal,
  inline = false,
  columnMode = false,
  newChatSignal,
  onClose,
  open: controlledOpen,
  onToggle,
}) {
  const isControlled = controlledOpen !== undefined;
  const user = useSelector((state) => state.user);
  const stores = useSelector((state) => state.stores.stores);
  const selectedStore = useSelector((state) => state.stores.selectedStore);

  const isAdmin = ADMIN_ROLES.includes(user.role);
  const helpBase = portal === 'client' ? '/client/help' : '/help';
  const dispatch = useDispatch();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const toggleOpen = isControlled ? onToggle : () => setInternalOpen((v) => !v);
  const closeOpen = isControlled ? onToggle : () => setInternalOpen(false);
  const [sessionList, setSessionList] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const activeSessionId = useSelector((state) => state.ui.savvybotActiveSessionId);
  const messages = useSelector((state) => state.ui.savvybotMessages);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [scopedStoreId, setScopedStoreId] = useState(
    () => selectedStore?.storeId ?? selectedStore?.id ?? null,
  );
  const [showSessions, setShowSessions] = useState(() => !columnMode);

  const { secondsLeft, applyRateLimit, isRateLimited } = useRateLimit();
  const threadRef = useRef(null);
  const inputRef = useRef(null);

  const panelVisible = inline || columnMode || open;

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    const res = await savvybotGetSessions({ limit: 20 });
    setSessionsLoading(false);
    if (res?.success !== false) {
      const list = res?.sessions ?? res?.data ?? [];
      setSessionList(list);
      return list;
    }
    return [];
  }, []);

  const handleOpenSession = useCallback(
    async (sessionId) => {
      dispatch(setSavvybotActiveSessionId(sessionId));
      dispatch(setSavvybotMessages([]));
      setMessagesLoading(true);
      setSendError(null);
      const res = await savvybotGetMessages(sessionId);
      setMessagesLoading(false);
      if (res?.success !== false) {
        const raw = res?.messages ?? res?.data ?? [];
        dispatch(setSavvybotMessages(raw.map(normalizeHistoryMessage)));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!panelVisible) return;
    loadSessions().then((list) => {
      if (columnMode && list && list.length > 0 && !activeSessionId && messages.length === 0) {
        const first = list[0];
        const sid = first.id ?? first.sessionId;
        handleOpenSession(sid);
      }
    });
  }, [panelVisible, loadSessions, columnMode, handleOpenSession, activeSessionId, messages.length]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (panelVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [panelVisible, activeSessionId]);

  const handleNewChat = useCallback(() => {
    dispatch(setSavvybotActiveSessionId(null));
    dispatch(setSavvybotMessages([]));
    setSendError(null);
    inputRef.current?.focus();
  }, [dispatch]);

  // Respond to newChatSignal fired from the header "New chat" button
  const prevSignalRef = useRef(newChatSignal);
  useEffect(() => {
    if (newChatSignal === undefined) return;
    if (newChatSignal !== prevSignalRef.current) {
      prevSignalRef.current = newChatSignal;
      handleNewChat();
    }
  }, [newChatSignal, handleNewChat]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || sending || isRateLimited) return;

    const userMsg = { role: 'user', content: text, id: `tmp-${Date.now()}` };
    dispatch(appendSavvybotMessage(userMsg));
    setInput('');
    setSending(true);
    setSendError(null);

    const body = { question: text };
    if (activeSessionId) body.sessionId = activeSessionId;
    if (isAdmin && scopedStoreId) body.storeId = scopedStoreId;
    if (inline) body.intent = 'docs';

    const res = await savvybotAsk(body);
    setSending(false);

    if (res?._status === 429) {
      const retryAfter = Number(res.retryAfter) || 60;
      applyRateLimit(retryAfter);
      setSendError(null);
      dispatch(
        appendSavvybotMessage({
          role: 'assistant',
          intent: 'refusal',
          prose: res.message || 'Too many requests. Please wait.',
          id: `err-${Date.now()}`,
        }),
      );
      return;
    }

    if (res?.success === false || res?._status >= 400) {
      setSendError(res?.message || 'Something went wrong. Please try again.');
      dispatch(filterSavvybotMessage(userMsg.id));
      setInput(text);
      return;
    }

    const sessionId = res?.sessionId ?? res?.session_id ?? activeSessionId;
    if (sessionId && !activeSessionId) {
      dispatch(setSavvybotActiveSessionId(sessionId));
      setSessionList((prev) => {
        const exists = prev.some((s) => (s.id ?? s.sessionId) === sessionId);
        if (exists) return prev;
        return [
          {
            id: sessionId,
            sessionId,
            title: text,
            preview: text,
            lastMessageAt: new Date().toISOString(),
          },
          ...prev,
        ];
      });
    }

    const assistantMsg = normalizeAskResponse({
      ...res,
      id: res?.messageId ?? `bot-${Date.now()}`,
    });
    dispatch(appendSavvybotMessage(assistantMsg));
  }, [
    input,
    sending,
    isRateLimited,
    activeSessionId,
    isAdmin,
    scopedStoreId,
    applyRateLimit,
    inline,
    dispatch,
  ]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const storeOptions = Array.isArray(stores) ? stores : [];

  // Shared panel body content — used in both inline and column modes
  // fullWidth=true renders the sessions list as the sole full-panel view (column mode)
  const buildSessionsSidebar = (onSelect, fullWidth = false) => (
    <aside className={`savvybot-sessions${fullWidth ? ' savvybot-sessions--full' : ''}`}>
      <button
        className="savvybot-new-chat"
        onClick={() => {
          handleNewChat();
          if (fullWidth) setShowSessions(false);
        }}>
        + New chat
      </button>
      <div className="savvybot-sessions__list">
        {sessionsLoading && <div className="savvybot-sessions__loading">Loading…</div>}
        {!sessionsLoading && sessionList.length === 0 && (
          <div className="savvybot-sessions__empty">No previous chats</div>
        )}
        {sessionList.map((s) => {
          const sid = s.id ?? s.sessionId;
          return (
            <button
              key={sid}
              className={`savvybot-sessions__item${activeSessionId === sid ? ' savvybot-sessions__item--active' : ''}`}
              onClick={() => onSelect(sid)}>
              <span className="savvybot-sessions__item-preview">
                {s.title ?? s.preview ?? s.firstMessage ?? 'Chat'}
              </span>
              {(s.lastMessageAt ?? s.updatedAt) && (
                <span className="savvybot-sessions__item-date">
                  {fmtDate(s.lastMessageAt ?? s.updatedAt)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </aside>
  );

  const threadArea = (emptyIcon, emptyText, placeholder) => (
    <div className="savvybot-thread-wrap">
      <div className="savvybot-thread" ref={threadRef}>
        {messages.length === 0 && !messagesLoading && (
          <div className="savvybot-thread__empty">
            <div className="savvybot-thread__empty-icon">{emptyIcon}</div>
            <div className="savvybot-thread__empty-text">{emptyText}</div>
          </div>
        )}
        {messagesLoading && <div className="savvybot-thread__loading">Loading conversation…</div>}
        {messages.map((msg, i) => (
          <div key={msg.id ?? i} className={`savvybot-msg savvybot-msg--${msg.role}`}>
            {msg.role === 'assistant' ? (
              <AssistantMessage msg={msg} helpBase={helpBase} />
            ) : (
              <p className="savvybot-prose">{msg.content}</p>
            )}
          </div>
        ))}
        {sending && (
          <div className="savvybot-msg savvybot-msg--assistant savvybot-msg--typing">
            <span className="savvybot-typing-dot" />
            <span className="savvybot-typing-dot" />
            <span className="savvybot-typing-dot" />
          </div>
        )}
      </div>

      {sendError && <div className="savvybot-send-error">{sendError}</div>}

      <div className="savvybot-input-row">
        <textarea
          ref={inputRef}
          className="savvybot-input"
          placeholder={isRateLimited ? `Rate limited — wait ${secondsLeft}s` : placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending || isRateLimited}
          rows={1}
          aria-label="Message input"
        />
        <button
          className="savvybot-send-btn"
          onClick={handleSend}
          disabled={sending || isRateLimited || !input.trim()}
          aria-label="Send message">
          {isRateLimited ? `${secondsLeft}s` : '↑'}
        </button>
      </div>
    </div>
  );

  if (inline) {
    return (
      <div
        className="savvybot-panel savvybot-panel--inline"
        role="complementary"
        aria-label="SavvyBot 3PL docs assistant">
        <div className="savvybot-panel__header">
          <div className="savvybot-panel__title">{BOT_LABEL}</div>
          <div className="savvybot-panel__header-actions">
            <button
              className="savvybot-panel__toggle-sessions"
              onClick={() => setShowSessions((v) => !v)}
              aria-label={showSessions ? 'Hide chat history' : 'Show chat history'}
              title={showSessions ? 'Hide history' : 'Show history'}>
              ☰
            </button>
          </div>
        </div>

        <div className="savvybot-panel__body">
          {showSessions && buildSessionsSidebar(handleOpenSession)}
          {threadArea(
            '📖',
            <>Ask {BOT_LABEL} anything about the docs and guides.</>,
            'Ask about the docs…',
          )}
        </div>
      </div>
    );
  }

  if (columnMode) {
    return (
      <div
        className="savvybot-panel savvybot-panel--column"
        role="complementary"
        aria-label="SavvyBot 3PL chat">
        <div className="savvybot-panel__header">
          <div className="savvybot-panel__title">{BOT_LABEL}</div>
          <div className="savvybot-panel__header-actions">
            {isAdmin && storeOptions.length > 0 && (
              <select
                className="savvybot-store-picker"
                value={scopedStoreId ?? ''}
                onChange={(e) => setScopedStoreId(e.target.value || null)}
                aria-label="Scope to store">
                <option value="">All stores</option>
                {storeOptions.map((s) => {
                  const id = s.storeId ?? s.id;
                  return (
                    <option key={id} value={id}>
                      {s.name ?? s.storeName ?? id}
                    </option>
                  );
                })}
              </select>
            )}
            <button
              className="savvybot-panel__toggle-sessions"
              onClick={() => setShowSessions((v) => !v)}
              aria-label={showSessions ? 'Hide chat history' : 'Show chat history'}
              title={showSessions ? 'Hide history' : 'Show history'}>
              ☰
            </button>
            <button
              className="savvybot-panel__close"
              onClick={onClose ?? closeOpen}
              aria-label="Close SavvyBot"
              title="Close SavvyBot">
              ✕
            </button>
          </div>
        </div>

        <div className="savvybot-panel__body">
          {showSessions
            ? buildSessionsSidebar((sid) => {
                handleOpenSession(sid);
                setShowSessions(false);
              }, true)
            : threadArea(
                '💬',
                <>Ask {BOT_LABEL} anything about your 3PL operations.</>,
                'Ask a question…',
              )}
        </div>
      </div>
    );
  }

  return (
    <>
      {!isControlled && (
        <button
          className={`savvybot-launcher${open ? ' savvybot-launcher--active' : ''}`}
          onClick={toggleOpen}
          aria-label="Open SavvyBot 3PL chat assistant"
          title="SavvyBot 3PL">
          <span className="savvybot-launcher__icon" aria-hidden="true">
            {open ? '✕' : '💬'}
          </span>
          <span className="savvybot-launcher__label">{BOT_LABEL}</span>
        </button>
      )}

      {open && (
        <div className="savvybot-panel" role="dialog" aria-label="SavvyBot 3PL chat">
          <div className="savvybot-panel__header">
            <div className="savvybot-panel__title">{BOT_LABEL}</div>
            <div className="savvybot-panel__header-actions">
              {isAdmin && storeOptions.length > 0 && (
                <select
                  className="savvybot-store-picker"
                  value={scopedStoreId ?? ''}
                  onChange={(e) => setScopedStoreId(e.target.value || null)}
                  aria-label="Scope to store">
                  <option value="">All stores</option>
                  {storeOptions.map((s) => {
                    const id = s.storeId ?? s.id;
                    return (
                      <option key={id} value={id}>
                        {s.name ?? s.storeName ?? id}
                      </option>
                    );
                  })}
                </select>
              )}
              <button
                className="savvybot-panel__toggle-sessions"
                onClick={() => setShowSessions((v) => !v)}
                aria-label={showSessions ? 'Hide chat history' : 'Show chat history'}
                title={showSessions ? 'Hide history' : 'Show history'}>
                ☰
              </button>
              <button className="savvybot-panel__close" onClick={closeOpen} aria-label="Close">
                ✕
              </button>
            </div>
          </div>

          <div className="savvybot-panel__body">
            {showSessions
              ? buildSessionsSidebar((sid) => {
                  handleOpenSession(sid);
                  setShowSessions(false);
                }, true)
              : threadArea(
                  '💬',
                  <>Ask {BOT_LABEL} anything about your 3PL operations.</>,
                  'Ask a question…',
                )}
          </div>
        </div>
      )}
    </>
  );
}

export default SavvyBot;
